import { Fragment, ReactNode } from "react";
import { Link, LinkProps, NavLink, Outlet } from "react-router-dom";
import styled, { css } from "styled-components";

import {
	faBell,
	faBox,
	faChartLine,
	faCirclePlus,
	faFileLines,
	faFlag,
	faGift,
	faLayerGroup,
	faLightbulb,
	faListCheck,
	faPaperPlane,
	faReceipt,
	faRss,
	faShieldHalved,
	faUsers,
	faWarning,
	IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import { SecondTitle, Title } from "components/atoms/Typography";
import Footer from "components/molecules/Footer";
import useAccess from "hooks/useAccess";
import { isDevEnv } from "utils/Utils";

interface NavLinkConfig {
	label: string;
	path: string;
	icon: IconDefinition;
	roles: string[];
	end?: boolean;
}

// Overskrift på toppnivå er en gruppe (MittNTE, Strømbestilling), ett nivå ned er det en seksjon (Innhold, Features)
interface NavHeadingConfig {
	title: string;
	children: NavConfig[];
}

type NavConfig = NavLinkConfig | NavHeadingConfig;

const NAV: NavConfig[] = [
	{
		title: "MittNTE",
		children: [
			{ label: "Brukere", path: "/users", icon: faUsers, roles: ["Read", "Admin"] },
			{ label: "Intercom segmenter", path: "/intercom-segments", icon: faRss, roles: ["Admin"] },
			{
				title: "Innhold",
				children: [
					{ label: "Feedkort", path: "/content", icon: faBell, roles: ["Content", "Admin"] },
					{ label: "Infotekster", path: "/info-text", icon: faFileLines, roles: ["Content", "Admin"] },
					{ label: "Vil du vite mer", path: "/vil-du-vite-mer", icon: faLightbulb, roles: ["Content", "Admin"] },
					{ label: "Driftsmeldinger", path: "/service-messages", icon: faWarning, roles: ["Content", "Admin"] },
				],
			},
			{
				title: "Features",
				children: [
					{ label: "Feature flags", path: "/features", icon: faFlag, roles: ["Admin"] },
					{ label: "Brukersegment", path: "/groups", icon: faLayerGroup, roles: ["Admin"] },
					{ label: "Statistikk", path: "/statistics", icon: faChartLine, roles: ["Admin"] },
					{ label: "Send push", path: "/notifications", icon: faBell, roles: ["Admin"] },
				],
			},
		],
	},
	{
		title: "Strømbestilling",
		children: [
			{ label: "Produktadmin", path: "/products", icon: faBox, roles: ["Product", "Admin"], end: true },
			{ label: "Sikring", path: "/sikring", icon: faShieldHalved, roles: ["manage-sikring"] },
			{
				title: "Strømtilbud",
				children: [
					{ label: "Opprett tilbud", path: "/orders/offers/new", icon: faCirclePlus, roles: ["view-orders", "Admin", "update-users"], end: true },
					{
						label: "Alle tilbud",
						path: "/orders/offers",
						icon: faListCheck,
						roles: ["view-orders", "Admin", "update-users"],
						end: true,
					},
				],
			},
			{
				title: "Bestillinger",
				children: [
					{ label: "Alle bestillinger", path: "/orders/all", icon: faReceipt, roles: ["view-orders", "Admin"] },
					{ label: "Vervinger", path: "/orders/referrals", icon: faGift, roles: ["view-orders", "Admin"] },
					{ label: "Send bestillingslenke", path: "/orders/send-link", icon: faPaperPlane, roles: ["manage-order", "admin-order"] },
				],
			},
		],
	},
];

const isLink = (node: NavConfig): node is NavLinkConfig => "path" in node;

// Fjerner lenker brukeren ikke har tilgang til, og overskrifter som da står tomme
function filterNav(nodes: NavConfig[], access: string[]): NavConfig[] {
	return nodes.flatMap<NavConfig>((node) => {
		if (isLink(node)) return node.roles.some((role) => access.includes(role)) ? [node] : [];
		const children = filterNav(node.children, access);
		return children.length > 0 ? [{ ...node, children }] : [];
	});
}

function NavTree({ nodes, depth }: Readonly<{ nodes: NavConfig[]; depth: number }>) {
	return nodes.map((node) =>
		isLink(node) ? (
			<NavItem key={node.path} to={node.path} end={node.end}>
				<FontAwesomeIcon icon={node.icon} fixedWidth />
				{node.label}
			</NavItem>
		) : (
			<Fragment key={node.title}>
				{depth === 0 ? <GroupTitle>{node.title}</GroupTitle> : <SectionTitle>{node.title}</SectionTitle>}
				<NavTree nodes={node.children} depth={depth + 1} />
			</Fragment>
		)
	);
}

function NoAccess() {
	return (
		<NoAccessWrapper>
			<h1 style={{ marginBottom: "10px" }}>Ingen tilgang</h1>
			<p>
				Du har ikke tilgang til denne siden.
				<br />
				Vennligst ta kontakt med vårt servicesenter hvis du mener dette er feil.
			</p>
		</NoAccessWrapper>
	);
}

export default function MainLayout() {
	const access = useAccess();
	const isDev = isDevEnv();

	const nav = filterNav(NAV, access);

	return (
		<Shell>
			<Sidebar $isDev={isDev}>
				<Brand to="/" title="Gå til forsiden">
					<img src="/assets/icons/nte-logo-white.svg" alt="NTE" />
					{isDev && <DevBadge>DEV</DevBadge>}
				</Brand>
				<Nav>
					{nav.map((node) => (
						<NavGroup key={isLink(node) ? node.path : node.title}>
							<NavTree nodes={[node]} depth={0} />
						</NavGroup>
					))}
				</Nav>
			</Sidebar>
			<Main>
				<Content>
					{access.length > 0 ? <Outlet /> : <NoAccess />}
					<Footer />
				</Content>
			</Main>
		</Shell>
	);
}

const Shell = styled.div`
	display: flex;
	min-height: 100vh;
`;

// Grønn meny i dev (som på main), så man ikke forveksler miljøene. Det finnes ingen grønn token mellom moerk og morkest, så hover blandes
const SIDEBAR_COLORS = css`
	--sidebar-bg: var(--nte-blaa-logo);
	--sidebar-hover: var(--nte-blaa-uu);
	--sidebar-active: var(--nte-blaa-mork);
	--sidebar-line: var(--nte-blaa-lys);
	--sidebar-muted: var(--nte-blaa-20);
`;

const SIDEBAR_COLORS_DEV = css`
	--sidebar-bg: var(--nte-groenn-moerk);
	--sidebar-hover: color-mix(in srgb, var(--nte-groenn-moerk), var(--nte-groenn-morkest));
	--sidebar-active: var(--nte-groenn-morkest);
	--sidebar-line: var(--nte-groenn);
	--sidebar-muted: var(--nte-groenn-20);
`;

// Sticky med fast høyde: menyen scroller for seg selv, mens siden fortsatt scroller på window (sider bruker window.scrollTo)
// Sticky med fast høyde: menyen scroller for seg selv, mens siden fortsatt scroller på window (sider bruker window.scrollTo)
const Sidebar = styled.aside<{ $isDev: boolean }>`
	${(props) => (props.$isDev ? SIDEBAR_COLORS_DEV : SIDEBAR_COLORS)}
	position: sticky;
	top: 0;
	align-self: flex-start;
	display: flex;
	flex-direction: column;
	flex: none;
	width: 240px;
	height: 100vh;
	background: var(--sidebar-bg);
`;

const Brand = styled(Link)`
	display: flex;
	align-items: center;
	gap: 10px;
	flex: none;
	height: 60px;
	padding: 0 18px;
	border-bottom: 1px solid var(--sidebar-line);
	text-decoration: none;

	img {
		height: 26px;
	}
`;

const DevBadge = styled.span`
	padding: 2px 8px;
	border: 1px solid var(--sidebar-muted);
	border-radius: 999px;
	color: var(--nte-graa-lys);
	font-size: 0.75rem;
	font-weight: 500;
`;

// Skjult scrollbar: nettleserens hvite spor skiller seg ut mot menyen. Scrolling fungerer fortsatt
const Nav = styled.nav`
	flex: 1;
	overflow-y: auto;
	padding: 8px;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`;

const NavGroup = styled.div`
	& + & {
		margin-top: 10px;
		padding-top: 10px;
		border-top: 1px solid var(--sidebar-line);
	}
`;

// Ekte overskrift for skjermlesere. Overstyrer de globale h2-stilene i GlobalStyling
const GroupTitle = styled.h2`
	margin: 0;
	padding: 8px 12px 6px;
	color: var(--nte-graa-lys);
	font-size: 1.125rem;
	font-weight: 700;
	line-height: 1.75rem;
	letter-spacing: normal;
`;

const SectionTitle = styled.div`
	padding: 14px 12px 6px;
	color: var(--sidebar-muted);
	font-size: 0.75rem;
	font-weight: 500;
	letter-spacing: 0.06em;
	text-transform: uppercase;
`;

const NavItem = styled(NavLink)`
	display: flex;
	align-items: center;
	gap: 11px;
	height: 40px;
	padding: 0 12px;
	margin-bottom: 2px;
	border-radius: 8px;
	color: var(--nte-graa-lys);
	font-size: 0.9375rem;
	font-weight: 500;
	white-space: nowrap;
	overflow: hidden;
	text-decoration: none;

	&:hover {
		background: var(--sidebar-hover);
		color: var(--nte-graa-lys);
	}

	&.active {
		background: var(--sidebar-active);
		color: var(--nte-graa-lys);
	}
`;

const Main = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	min-width: 0;
`;

const Content = styled.main`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: var(--grid-4);
`;

const NoAccessWrapper = styled.div`
	padding: 48px;
`;

export const Intro = styled.h2`
	margin-bottom: var(--grid-2);
	text-align: center;
	${(props) =>
		props.theme.media.desktop(css`
			font-size: 20px;
			margin-bottom: 5px;
		`)}
`;

export function MainLayoutPageHeader({
	title,
	intro,
	link,
	loading,
}: Readonly<{
	title?: ReactNode;
	intro?: ReactNode;
	link?: LinkProps & { label: ReactNode };
	loading?: boolean;
}>) {
	return (
		<HeaderModalWrapper gap="6px" direction={Direction.column} style={{ marginBottom: "10px" }}>
			{title && <Title loading={loading}>{title}</Title>}
			{intro && <SecondTitle loading={loading}>{intro}</SecondTitle>}
			{link && <Link {...link}>{link.label}</Link>}
		</HeaderModalWrapper>
	);
}

const HeaderModalWrapper = styled(Flex)``;
